'use client';

import Link from 'next/link';
import { Brain } from 'lucide-react';

type SiteFooterProps = {
  /** Public marketing/legal pages may show Sign in */
  showSignIn?: boolean;
  /** Override sign-in target (e.g. #sign-in on the landing page) */
  signInHref?: string;
  /** Constrain width for centered public layouts */
  contained?: boolean;
  className?: string;
};

export default function SiteFooter({
  showSignIn = false,
  signInHref = '/login',
  contained = false,
  className = '',
}: SiteFooterProps) {
  const year = new Date().getFullYear();

  return (
    <footer
      className={`relative z-[1] border-t border-white/[0.06] mt-auto ${className}`}
      role="contentinfo"
    >
      <div
        className={`px-5 sm:px-8 py-8 sm:py-10 ${
          contained ? 'mx-auto max-w-6xl' : 'w-full'
        }`}
      >
        <div className="flex flex-col gap-8 sm:gap-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <span className="page-header-icon !h-8 !w-8 !rounded-lg">
                <Brain className="w-4 h-4" strokeWidth={1.75} aria-hidden />
              </span>
              <span className="text-sm font-semibold tracking-tight text-white">
                BrainScraper
              </span>
            </div>

            <nav
              aria-label="Footer"
              className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400"
            >
              <Link href="/terms" className="hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <Link href="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              {showSignIn &&
                (signInHref.startsWith('#') ? (
                  <a href={signInHref} className="hover:text-white transition-colors">
                    Sign in
                  </a>
                ) : (
                  <Link href={signInHref} className="hover:text-white transition-colors">
                    Sign in
                  </Link>
                ))}
            </nav>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-5 border-t border-white/[0.06] text-xs text-gray-500">
            <span>© {year} Brainscraper LLC. All rights reserved.</span>
            <span className="text-gray-600">
              Enriched data may not be used for outreach without required consent.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
